import { toast } from "sonner";
import { Check, Package, Truck, Sparkles, Flag } from "lucide-react";
import { TRIAL_STEPS, memberApi, useMemberMutation, errorMessage } from "@/hooks/useMember";

const icons = { awaiting_dispatch: Package, dispatched: Truck, in_progress: Sparkles, completed: Flag };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null);

export default function TrialTracker({ trial, compact = false }) {
  const confirm = useMemberMutation(memberApi.confirmDelivery, () => toast.success("Delivery confirmed. Your private journal is now open."));
  const idx = trial.status === "withdrawn" ? -1 : TRIAL_STEPS.findIndex((s) => s.key === trial.status);

  return (
    <div className="border border-forest/15 p-5 sm:p-6 bg-ivory" data-testid={`trial-tracker-${trial.trial_id}`} data-status={trial.status}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-[10px] uppercase tracking-[0.22em] text-champagne">Trial tracking</p>
        {trial.status === "withdrawn" && <span className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50">Withdrawn</span>}
      </div>
      <ol className="mt-5 grid grid-cols-4 gap-2">
        {TRIAL_STEPS.map((s, i) => {
          const Icon = icons[s.key];
          const done = i < idx, current = i === idx;
          return (
            <li key={s.key} className="relative">
              <div className={`h-px w-full mb-3 ${i <= idx ? "bg-forest" : "bg-forest/15"}`} />
              <div className={`flex items-center gap-2 ${current ? "text-forest" : done ? "text-moss" : "text-charcoal/35"}`}>
                {done ? <Check size={14} /> : <Icon size={14} strokeWidth={1.5} />}
                <span className="text-[10px] uppercase tracking-[0.18em] hidden sm:inline">{s.label}</span>
              </div>
            </li>
          );
        })}
      </ol>
      {!compact && (
        <div className="mt-5 text-sm text-charcoal/70 font-light">
          {trial.status === "awaiting_dispatch" && <p>ELA is preparing your product. Dispatch and tracking details will appear here.</p>}
          {trial.status === "dispatched" && (
            <div>
              <p>Dispatched {fmt(trial.tracking?.dispatched_at)}{trial.tracking?.carrier && ` via ${trial.tracking.carrier}`}.
                {trial.tracking?.tracking_number && <> Tracking: <span className="font-medium text-charcoal" data-testid="tracking-number">{trial.tracking.tracking_number}</span></>}</p>
              <button onClick={() => confirm.mutate(trial.trial_id, { onError: (e) => toast.error(errorMessage(e)) })} className="btn btn-primary mt-4" data-testid="confirm-delivery-button">It has arrived</button>
            </div>
          )}
          {trial.status === "in_progress" && <p>Started {fmt(trial.started_at)}. Record your private check-ins in the Journal tab.</p>}
          {trial.status === "completed" && <p>Completed {fmt(trial.completed_at)}. Thank you for sharing your experience.</p>}
        </div>
      )}
    </div>
  );
}
